
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Database } from "@/integrations/supabase/types";

type SubscriptionTier = Database["public"]["Enums"]["subscription_tier"];

export const AddBetaTesterForm = ({ onSuccess }: { onSuccess: () => void }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [tier, setTier] = useState<SubscriptionTier>("essentials");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // 1. Sign up the user with Supabase Auth
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
      });

      if (signUpError) throw signUpError;
      if (!signUpData.user) throw new Error("Failed to create user");

      // 2. Create a beta tester subscription for the user
      const { error: subscriptionError } = await supabase.rpc('create_beta_tester_subscription', {
        user_id_param: signUpData.user.id,
        tier_param: tier
      });

      if (subscriptionError) throw subscriptionError;

      toast.success("Beta tester account created successfully");
      setEmail("");
      setPassword("");
      setTier("essentials");
      onSuccess();
    } catch (error: any) {
      console.error("Error creating beta tester:", error);
      toast.error(error.message || "Failed to create beta tester account");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-4 border rounded-lg bg-white shadow-sm">
      <h2 className="text-xl font-semibold mb-4">Add Beta Tester Account</h2>
      
      <div className="space-y-2">
        <Label htmlFor="beta-email">Email</Label>
        <Input
          id="beta-email"
          type="email"
          placeholder="Enter email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="beta-password">Password</Label>
        <Input
          id="beta-password"
          type="password"
          placeholder="Enter password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="beta-tier">Subscription Tier</Label>
        <Select value={tier} onValueChange={(value: SubscriptionTier) => setTier(value)}>
          <SelectTrigger id="beta-tier">
            <SelectValue placeholder="Select tier" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="essentials">Essentials</SelectItem>
            <SelectItem value="pro">Pro</SelectItem>
            <SelectItem value="elite">Elite</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <Button type="submit" className="w-full" disabled={isLoading}>
        {isLoading ? "Creating..." : "Create Beta Tester Account"}
      </Button>
    </form>
  );
};
